<template>
  <div :class="{ disabled: disabled }">
    <Loading v-if="!loadStatus" text="Loading gifts" />

    <template v-if="loadStatus && productList.length">
      <RemoveTierModal
        :showFooter="true"
        :modalName="removeTierModalName"
        v-show="isModalActive"
        @confirmAction="cofirmRemoveTierFromGroupOrder($event)"
        colorVariation="secondary"
      >
        <template slot="header">
          <h4>Are you sure you want to remove this tier?</h4>
        </template>
        <template slot="body">
          <span> </span>
        </template>
      </RemoveTierModal>

      <section v-if="sequence <= 1">
        <GroupOrderHeaderBanner
          :title="'Set up your store'"
          :headerText="'Tier 1'"
          :currentStep="creatingGroupOrderStep"
          @onPrevStep="$emit('handlePrevStep')"
          @onNextStep="$emit('handleNextStep')"
        />
      </section>
      <section v-if="sequence !== 1">
        <GroupOrderTierHeader
          :title="`Tier ${sequence}`"
          :disabled="disabled"
          @onRemoveTier="showRemoveTierFromGroupOrderModal"
        />
      </section>

      <div class="tier-details">
        <div
          class="flex flex-col md:flex-row gap-4 items-start md:items-center"
        >
          <div class="form-label">
            <p><strong>Tier name (Optional) </strong></p>
          </div>
          <div class="form-input-value input-value">
            <input
              autocomplete="off"
              :placeholder="`Tier ${sequence}`"
              :value="`${name}`"
              type="text"
              class="form-control"
              @blur="updateTierName($event)"
            />
          </div>
        </div>
        <div class="flex gap-4 flex-wrap items-center">
          <div class="form-label">
            <p><strong>Number of products a user can choose</strong></p>
          </div>
          <div
            class="form-input-value input-value flex flex-wrap md:flex-nowrap items-center gap-6"
          >
            <div class="flex items-center gap-10">
              <radio-button
                label="Up to"
                value="UPTO"
                v-model="computedSelectionType"
                :isGroupOrder="true"
                :isTransparent="true"
                custom-styles="font-size: 0.813rem; width:max-content"
                @change="updateSelectionType('UPTO')"
              />
              <radio-button
                label="Exactly"
                value="EXACTLY"
                v-model="computedSelectionType"
                :isGroupOrder="true"
                :isTransparent="true"
                custom-styles="font-size: 0.813rem"
                @change="updateSelectionType('EXACTLY')"
              />
            </div>
            <input
              autocomplete="off"
              :placeholder="`Enter value`"
              type="number"
              class="form-control w-max"
              style="width: max-content;"
              :value="tierQuantityLimit"
              @input="updateTier($event)"
            />
          </div>
        </div>
      </div>
      <div class="flex gap-4 items-center margin-top-3 tier-product-info">
        <div class="form-label">
          <p>
            <strong
              >Add products to this tier by selecting from items below:</strong
            >
          </p>
        </div>
      </div>

      <div class="tier-gallery">
        <div
          class="gallery-image"
          v-for="product in productList"
          :key="product.id"
        >
          <div
            class="portrait"
            :class="{
              'is-selected': isSelectedOrTemporallySelected(product)
            }"
            @click="selectItem($event, product)"
          >
            <div class="tier-checkbox">
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  :checked="isSelectedOrTemporallySelected(product)"
                  @change.stop="selectItem($event, product)"
                />
                <span class="checkmark checkmark-center"></span>
              </label>
            </div>
            <img :src="photoURL(product)" @error="handleBrokenImage($event)" />
          </div>
          <div class="portrait-info">
            <div>
              <p>{{ product.attributes.name }}</p>
              <span v-show="showPrices">{{
                product.attributes.retailPrice
                  ? formatCurrency(product.attributes.retailPrice)
                  : formatCurrency(0)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Util from '../../mixins/Util'
import RadioButton from '@/components/Universal/RadioButton.vue'
import GroupOrderTierHeader from './GroupOrderTierHeader.vue'
import GroupOrderHeaderBanner from './GroupOrderHeaderBanner'

import {
  progressStatus,
  defaultNotFoundImage,
  isCard,
  handleBrokenImage
} from '../../helpers'

export default {
  name: 'GroupOrderTierGallery',
  components: {
    RemoveTierModal: () => import('@/components/Universal/RemoveModal'),
    RadioButton,
    GroupOrderTierHeader,
    GroupOrderHeaderBanner
  },
  mixins: [Util],
  props: {
    sequence: {
      type: Number,
      required: true
    },
    groupOrderId: {
      type: Number,
      required: false
    },
    tierId: {
      type: Number,
      required: true
    },
    tierQuantityLimit: {
      type: String,
      required: true
    },
    selectionType: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    tierItems: {
      type: Array,
      required: true
    },
    defaultTiers: {
      type: Array
    },
    disabled: {
      type: Boolean,
      required: true
    }
  },
  data() {
    const quantities = []
    for (let q = 1; q <= 20; q++) {
      quantities.push(`Quantity - ${q}`)
    }
    return {
      quantities,
      selectedItems: [],
      removeTierModalName: 'removeTierModal',
      handleBrokenImage: handleBrokenImage,
      computedSelectionType: this.selectionType,
      defaultTierItems: []
    }
  },
  created() {
    this.$store.dispatch('groupOrder/getGroupOrderGallery')
    if (!this.selectionType) {
      this.updateSelectionType('EXACTLY')
    }
  },

  methods: {
    photoURL(product) {
      switch (product.type) {
        case 'products':
          if (product.attributes.defaultProductVariantImages.length) {
            return product.attributes.defaultProductVariantImages[0].url
          }
          break
        case 'bundles':
          if (product.attributes.images.length) {
            return product.attributes.images[0].url
          }
          break

        default:
          return defaultNotFoundImage
      }
    },
    selectItem(e, product) {
      e.preventDefault()
      // Check if product is already selected for tier
      let selectedProduct = this.tierItems.find(
        p => p.itemableId === product.id
      )

      // Check if product is  selected or temporary
      const isSelectedOrTemporallySelected = this.isSelectedOrTemporallySelected(
        product
      )

      if (isSelectedOrTemporallySelected) {
        // remove item from tier if item was already selected
        this.$store.commit('groupOrder/REMOVE_ITEM_FROM_TIER', {
          tierId: this.tierId,
          itemId: selectedProduct.itemId
        })

        // check is tier item is temporary selcted only
        const temporalSelectedItem = this.temporalSelectedItems.find(
          item => item.productId === product.id
        )
        if (temporalSelectedItem) {
          // remove tier item from temporary items
          this.$store.commit(
            'groupOrder/UNMARK_TEMPORALY_TIER_ITEM',
            product.id
          )
        }

        const isDefaultSelectesTierItem = this.defaultTiers.find(
          tierItem => tierItem.itemId === selectedProduct.itemId
        )

        if (isDefaultSelectesTierItem) {
          // save only unselectd default tiers to remove list
          this.$store.commit('groupOrder/SAVE_TIER_ITEMS_TO_REMOVE', {
            tierId: this.tierId,
            itemId: selectedProduct.itemId,
            productId: product.id,
            groupOrderId: this.groupOrderId
          })
        }
      } else {
        const selectedTierItemsToRemove = this.selectedTierItemsToRemove

        // check if tier item was selected to be removed
        const selectedTierItemToRemove = selectedTierItemsToRemove.find(
          tierItem => tierItem.itemId === product.id
        )
        // remove tier item from unsave list
        if (selectedTierItemToRemove) {
          this.$store.commit(
            'groupOrder/UNSAVE_TIER_ITEMS_TO_REMOVE',
            selectedTierItemToRemove.itemId
          )
        }

        // add new item to tier
        this.$store.commit('groupOrder/ADD_NEW_ITEM_TO_TIER', {
          tierId: this.tierId,
          newTierItem: {
            itemId: product.id,
            itemableId: product.id,
            itemableType: product.type === 'products' ? 'Product' : 'bundles'
          }
        })
        this.$store.commit('groupOrder/MARK_TEMPORALY_TIER_ITEM', {
          productId: product.id,
          tierId: this.tierId,
          productType: product.type === 'products' ? 'Product' : 'bundles'
        })
      }
    },

    isSelectedItem() {
      return {
        'is-selected': this.tierItems
      }
    },

    updateTier(e) {
      const tierQuantityLimit = e.target.value
      if (tierQuantityLimit) {
        let payload = {
          groupOrderId: this.groupOrderId,
          tierId: this.tierId,
          selectionType: this.selectionType,
          tierQuantityLimit: tierQuantityLimit,
          name: this.name
        }
        this.$store.dispatch('groupOrder/updateGroupOrderTier', payload)
      }
    },
    updateTierName(e) {
      const tierName = e.target.value
      let payload = {
        groupOrderId: this.groupOrderId,
        tierId: this.tierId,
        selectionType: this.selectionType,
        tierQuantityLimit: this.tierQuantityLimit,
        name: tierName
      }
      this.$store.dispatch('groupOrder/updateGroupOrderTier', payload)
    },
    updateSelectionType(value) {
      let payload = {
        groupOrderId: this.groupOrderId,
        tierId: this.tierId,
        selectionType: value,
        tierQuantityLimit: this.tierQuantityLimit,
        name: this.name
      }
      this.$store.dispatch('groupOrder/updateGroupOrderTier', payload)
    },

    showRemoveTierFromGroupOrderModal() {
      this.$store.dispatch('modal/showRemoveModal', this.removeTierModalName)
    },

    cofirmRemoveTierFromGroupOrder() {
      this.$store.dispatch('groupOrder/removeTierFromGroupOrder', {
        groupOrderId: this.groupOrderId,
        tierId: this.tierId
      })
    },
    isSelectedOrTemporallySelected(product) {
      if (
        this.tierItems.some(p => p.itemableId === product.id) ||
        this.temporalSelectedItems.find(
          item => item.productId === product.id && item.tierId === this.tierId
        )
      ) {
        return true
      }

      return false
    }
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadProducts)
    },
    ...mapState({
      temporalSelectedItems: state => state.groupOrder.temporalSelectedItems,
      selectedTierItemsToRemove: state =>
        state.groupOrder.selectedTierItemsToRemove,
      groupOrderTiers: state => state.groupOrder.groupOrderTiers,
      creatingGroupOrderStep: state => state.groupOrder.creatingGroupOrderStep,
      loadProducts: state => state.groupOrder.loadProducts,
      productList: state =>
        state.groupOrder.groupOrderProducts.filter(product =>
          product.attributes.tagList
            ? !isCard(product.attributes.tagList)
            : product
        )
    }),

    ...mapGetters({
      activeModals: 'modal/activeModals'
    }),
    showPrices() {
      return this.$store.getters['team/showPrices']
    },
    isModalActive() {
      return this.activeModals(this.removeTierModalName)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.tier-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.6rem;
  border-bottom: 8px solid $main-6;

  .tier-name {
    font-size: 1.3rem;
    font-weight: 400;
  }

  .remove-icon {
    height: 20px;
    cursor: pointer;
  }
}

.tier-details {
  margin-top: 1.25rem;
  padding: 1.6rem 1.66rem;
  border: 1px solid $main-6;
  border-radius: 2px;
  margin-bottom: 2rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  .form-label {
    p {
      font-size: 0.838rem;
    }
  }
  input {
    height: max-content;
  }
}
.tier-product-info {
  p {
    font-size: 0.875rem;
  }
}

.tier-gallery {
  display: flex;
  flex-wrap: wrap;
  grid-column-gap: 1.75rem;
  padding-top: 2rem;
  margin-bottom: 1rem;
}

.gallery-image {
  width: 31%;
  margin-bottom: 2em;

  a {
    text-decoration: none;
  }

  a:hover {
    color: $onyx;
    text-decoration: none;
  }

  .tier-checkbox {
    position: relative;
    top: 0.8rem;
    left: 0.8rem;
  }

  .portrait {
    cursor: pointer;
    height: 290px;
    border: 1px solid $gray-variant-2;
    margin: 0 auto;
    position: relative;
    transition: border 0.3s linear;

    img {
      padding: 2rem;
      width: 100%;
      height: inherit;
      max-width: 240px;
      object-fit: contain;
      margin: 0 auto;
    }
  }

  .portrait-info {
    width: 100%;

    & > div {
      display: flex;
      color: $main-6;
      padding-top: 10px;

      p {
        width: 72%;
        text-align: left;
        font-size: 0.875rem;
        opacity: 0.8;
      }

      span {
        font-size: 0.875rem;
        margin-left: auto;
        opacity: 0.8;
      }
    }
  }
}

.is-selected {
  border: 2px solid $onyx !important;
}

@media screen and (max-width: 900px) and (min-width: 620px) {
  .gallery {
    padding-top: 2rem;
    margin-bottom: 2rem;
  }

  .gallery-image {
    flex-basis: 50%;
  }
}

@media screen and (max-width: 619px) and (min-width: 350px) {
  .gallery {
    flex-direction: column;
  }

  .gallery-image {
    flex-basis: 100%;
  }
}
</style>
